<template>
  <div class="kpi-row">
    <div v-for="tile in tiles" :key="tile.label" class="kpi-tile">
      <div class="kpi-tile__label">{{ tile.label }}</div>
      <div class="kpi-tile__value" :class="{ 'kpi-tile__value--negative': tile.value < 0 }">
        {{ formatValue(tile.value, tile.unit, tile.fraction) }}
      </div>
      <div class="kpi-tile__foot">
        <ComparisonBadge
          v-if="tile.previous !== null"
          :current="tile.value"
          :previous="tile.previous"
          :unit="tile.unit"
          :invert="tile.invert"
          :loading="comparisonLoading"
        />
        <svg
          v-if="tile.spark && tile.spark.length > 1"
          class="kpi-tile__spark"
          viewBox="0 0 100 28"
          preserveAspectRatio="none"
        >
          <polyline
            :points="sparkPoints(tile.spark)"
            fill="none"
            :stroke="tile.sparkColor || '#2a78d6'"
            stroke-width="2"
            stroke-linejoin="round"
            stroke-linecap="round"
            vector-effect="non-scaling-stroke"
          />
        </svg>
      </div>
    </div>
  </div>
</template>

<script>
import ComparisonBadge from "@/components/Statistics/components/ComparisonBadge";
import { getStatisticTotals } from "@/store/utils/statisticsUtils";
import { getDecimalNumberString } from "@/utils";
import { statisticsColors } from "@/config";

export default {
  name: "KpiRow",
  components: { ComparisonBadge },
  props: {
    data: { type: Object, required: true },
    comparison: { type: Object, default: null },
    comparisonLoading: { type: Boolean, default: false },
  },
  computed: {
    totals() {
      return this.metricsFor(this.data);
    },
    previousTotals() {
      return this.comparison ? this.metricsFor(this.comparison) : null;
    },
    /** Tages-Sparkline-Basis: Umsatz/Kosten je Tag über alle Shops */
    sparkSeries() {
      const sum = (getter) =>
        (this.data.shops || []).reduce((acc, shop) => {
          const timeline = getter(shop) || {};
          for (const key of Object.keys(timeline)) {
            acc[key] = (acc[key] || 0) + (timeline[key] || 0);
          }
          return acc;
        }, {});
      const revenue = sum((s) => s.timelines?.revenue);
      const costs = sum((s) => s.timelines?.costs);
      const keys = Object.keys(revenue);
      // Tages-Keys (nach den 24 Stunden-Buckets); bei 1-Tages-Sicht die Stunden
      const dayKeys = keys.length > 25 ? keys.slice(24) : keys.slice(0, 24);
      return {
        revenue: dayKeys.map((k) => revenue[k] || 0),
        profit: dayKeys.map((k) => (revenue[k] || 0) - (costs[k] || 0)),
      };
    },
    tiles() {
      const totals = this.totals;
      const prev = this.previousTotals;
      const value = (key) => totals[key];
      const previous = (key) => (prev ? prev[key] : null);
      return [
        { label: "Umsatz", value: value("revenue"), previous: previous("revenue"), unit: "€", spark: this.sparkSeries.revenue, sparkColor: statisticsColors.revenue },
        { label: "Gewinn", value: value("profit"), previous: previous("profit"), unit: "€", spark: this.sparkSeries.profit, sparkColor: statisticsColors.profit },
        { label: "Marge", value: value("margin"), previous: previous("margin"), unit: "%", fraction: 1 },
        { label: "Bestellungen", value: value("orders"), previous: previous("orders"), unit: "", fraction: 0 },
        { label: "Ø-Bestellwert", value: value("aov"), previous: previous("aov"), unit: "€" },
        { label: "MER", value: value("mer"), previous: previous("mer"), unit: "", fraction: 2 },
        { label: "KI-Kosten", value: value("aiCosts"), previous: previous("aiCosts"), unit: "€", invert: true },
      ];
    },
  },
  methods: {
    metricsFor(statistic) {
      const base = getStatisticTotals(statistic);
      const adSpend = (statistic.shops || []).reduce(
        (sum, shop) => sum + (shop.costs?.advertisement || 0), 0
      );
      return {
        ...base,
        margin: base.revenue ? (base.profit / base.revenue) * 100 : 0,
        aov: base.orders ? base.revenue / base.orders : 0,
        mer: adSpend ? base.revenue / adSpend : 0,
        aiCosts: statistic.aiCosts?.total || 0,
      };
    },
    formatValue(value, unit, fraction = 2) {
      return `${getDecimalNumberString(value || 0, fraction)}${unit ? ` ${unit}` : ""}`;
    },
    sparkPoints(values) {
      const min = Math.min(...values, 0);
      const max = Math.max(...values, 1);
      const span = max - min || 1;
      const stepX = 100 / (values.length - 1);
      return values
        .map((v, i) => `${(i * stepX).toFixed(1)},${(26 - ((v - min) / span) * 24).toFixed(1)}`)
        .join(" ");
    },
  },
};
</script>

<style lang="scss" scoped>
.kpi-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  margin-bottom: 14px;

  @media (max-width: 700px) {
    /* minmax(0,1fr): Spalten teilen exakt hälftig, unabhängig vom
       Min-Content der Kacheln (sonst wachsen sie über den Viewport) */
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }
}

.kpi-tile {
  min-width: 0; /* darf schrumpfen — Inhalt kürzt sich statt zu überlaufen */
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06), 0 4px 14px rgba(16, 24, 40, 0.07);
  padding: 12px 14px 10px;

  &__label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: rgba(0, 0, 0, 0.45);
  }

  &__value {
    font-size: 22px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    margin: 2px 0 4px;
    color: rgba(0, 0, 0, 0.85);

    &--negative {
      color: #c23434;
    }
  }

  &__foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-height: 28px;
  }

  &__spark {
    flex: 1;
    max-width: 90px;
    height: 28px;
    opacity: 0.8;
  }
}
</style>
